import { HEADER_HEIGHT } from 'constants/style';
import { debounce, isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Delivery, DeliveryConnection, DeliveryState } from 'types/api';
import { errorHandler } from 'utils/ErrorHandler';
import { useQueryGetDelivery } from '../graphQL/hooks/useQueryGetDelivery';
import { DeliveryParam, INITIAL_DELIVERY_PARAM } from '../graphQL/types';

export const useDeliveryHooks = () => {
  const { isLoading, deliveryData, refetch } = useQueryGetDelivery();

  const { width, height, ref } = useResizeDetector();

  const isMobile = width ? width < 768 : window.innerWidth < 768;

  const [deliveryList, setDeliveryList] = useState<DeliveryConnection>();
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery>();
  const [isOpenDeliveryDetail, setIsOpenDeliveryDetail] =
    useState<boolean>(false);
  const [deliveryParam, setDeliveryParam] = useState<DeliveryParam>(
    INITIAL_DELIVERY_PARAM,
  );
  const [page, setPage] = useState<number>(1);
  const [isSearch, setIsSearch] = useState<boolean>(false);

  const [windowHeight, setWindowHeight] = useState(
    document.documentElement.clientHeight - HEADER_HEIGHT,
  );

  const handleSelectDelivery = (delivery: Delivery) => {
    if (isEqual(delivery, selectedDelivery) && isOpenDeliveryDetail) {
      setIsOpenDeliveryDetail(false);
      setSelectedDelivery(undefined);

      return;
    }

    setIsOpenDeliveryDetail(true);
    setSelectedDelivery(delivery);
  };

  const handleCloseDeliveryDetail = () => {
    setIsOpenDeliveryDetail(false);
  };

  const handleChangeDeliveryParam = <T extends keyof DeliveryParam>(
    key: T,
    value: any,
  ) => {
    setDeliveryParam(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFilterClick = (value: DeliveryState | 'ALL') => {
    if (value === 'ALL') {
      handleChangeDeliveryParam('filter', {
        ...deliveryParam.filter,
        statusIn: null,
      });

      return;
    }

    if (deliveryParam.filter.statusIn?.includes(value as DeliveryState)) {
      handleChangeDeliveryParam('filter', {
        ...deliveryParam.filter,
        statusIn: deliveryParam.filter.statusIn?.filter(item => item !== value),
      });

      return;
    }

    if (deliveryParam.filter.statusIn?.length === 6) {
      handleChangeDeliveryParam('filter', {
        ...deliveryParam.filter,
        statusIn: null,
      });

      return;
    }

    handleChangeDeliveryParam('filter', {
      ...deliveryParam.filter,
      statusIn: deliveryParam.filter.statusIn
        ? [...deliveryParam.filter.statusIn, value]
        : [value],
    });

    return;
  };

  const handleAccpetSearchKeyword = (
    key: string,
    value: string,
    isAble: boolean,
  ) => {
    setDeliveryParam(prev => ({
      ...prev,
      filter: {
        ...prev.filter,
        [key]: value,
      },
    }));
    setIsSearch(isAble);
  };

  const handleRefetchDeliveryList = async () => {
    try {
      const response = await refetch({ page: 1, ...deliveryParam });

      if (response) {
        setPage(1);
        setDeliveryList(response.data.delivery.deliveries);

        if (selectedDelivery) {
          const findDelivery = response.data.delivery.deliveries.edges.find(
            item => item.node.id === selectedDelivery.id,
          );

          if (findDelivery) {
            setSelectedDelivery(findDelivery.node);
          }
        }
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleMoreDeliveryList = async () => {
    setPage(prev => {
      handleLoadMore(prev + 1);
      return prev + 1;
    });
  };

  const handleLoadMore = async (page: number) => {
    try {
      const response = await refetch({ page, ...deliveryParam });

      if (response) {
        setDeliveryList(prev => {
          return {
            edges: [...prev.edges, ...response.data.delivery.deliveries.edges],
            pageInfo: response.data.delivery.deliveries.pageInfo,
            totalCount: response.data.delivery.deliveries.totalCount,
          };
        });
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      setDeliveryList(deliveryData);
    }
  }, [isLoading]);

  useEffect(() => {
    handleRefetchDeliveryList();
  }, [deliveryParam]);

  useEffect(() => {
    const handleResize = () => {
      const viewportHeight = document.documentElement.clientHeight;

      setWindowHeight(viewportHeight - 81);
    };

    // `resize` 이벤트 리스너 추가
    window.addEventListener('resize', debounce(handleResize, 500));

    // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    ref,
    windowHeight,
    isMobile,
    deliveryList,
    deliveryParam,
    selectedDelivery,
    isOpenDeliveryDetail,
    isSearch,
    handleChangeDeliveryParam,
    handleRefetchDeliveryList,
    handleFilterClick,
    handleAccpetSearchKeyword,
    handleSelectDelivery,
    handleCloseDeliveryDetail,
    handleMoreDeliveryList,
    handleReload,
  };
};
